// Per-arm results for a started A/B test: page views, conversions, rate with a 95% interval,
// uplift over control, p-value and probability of beating control (maths in abStats.js).
// Rendered inside ABTestSection's .ab-test wrapper, whose abTesting.css styles it.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { abApi } from '../../utils/abApi';
import { buildResultRows } from '../../utils/abTesting';
import InfoTip from '../../cognitive-flows/components/shared/InfoTip';
import { RefreshIcon } from './abIcons';

// Each column's hover explanation. Keep the wording in step with how abStats.js computes it.
const HEADERS = [
    { label: 'Arm', tip: 'Control is your page as authored; each variant is the content its share of visitors sees.' },
    { label: 'Page views', tip: "Times visitors saw this arm's page in the selected dates." },
    { label: 'Conversions', tip: 'Page views that led to a click on a Key metric element (Goal tab).' },
    {
        label: 'Rate (95% CI)',
        tip: 'Conversions ÷ page views. The range underneath is the 95% confidence interval: the true rate very likely lies inside it.',
    },
    { label: 'Uplift', tip: "How much higher or lower this arm's rate is than Control's, relative to Control's rate." },
    {
        label: 'p-value',
        tip: 'Chance of a difference at least this large if the arm were really no different from Control. Under 0.05 is usually called significant.',
    },
    { label: 'P(beats control)', tip: "Estimated probability that this arm's true rate is higher than Control's." },
];
const cell = 'channel_performance_table_cell';

const isoDay = (msAgo = 0) => new Date(Date.now() - msAgo).toISOString().slice(0, 10);
const pct = (x, digits = 1) => (x == null ? '—' : `${(x * 100).toFixed(digits)}%`);

export default function ABResultsPanel({ app_url, campaignId, arms, variants }) {
    const [range, setRange] = useState({ from: isoDay(29 * 86400000), to: isoDay() });
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await abApi.get(app_url, campaignId, range));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [app_url, campaignId, range]);

    useEffect(() => { load(); }, [load]);

    const totals = useMemo(() => data?.metrics?.totals || {}, [data]);
    // buildResultRows runs a 5,000-draw Monte Carlo per arm: redo it only when its inputs change,
    // not on every keystroke in the goal or hypothesis fields above the panel.
    const rows = useMemo(() => buildResultRows(arms, variants, totals, { bayesIters: 5000 }), [arms, variants, totals]);

    return (
        <div className="ab-block ab-block--flush" data-testid="ab-results">
            <div className="ab-results-head">
                <h6 className="ab-section-title">Results</h6>
                <div className="ab-actions">
                    <input type="date" aria-label="From" className="form-control ab-date"
                        value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
                    <span className="ab-sub">to</span>
                    <input type="date" aria-label="To" className="form-control ab-date"
                        value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
                    <button type="button" className="btn ab-btn-secondary btn-height" onClick={load} disabled={loading}>
                        {loading
                            ? <span className="spinner-border spinner-border-sm" role="status" aria-label="Loading" />
                            : <RefreshIcon />}
                        Refresh
                    </button>
                </div>
            </div>
            {error && <p className="ab-error">{error}</p>}
            <div className="personalization-targeting-table">
                <table className="ab-table">
                    <thead>
                        <tr>
                            {HEADERS.map((h) => (
                                <th key={h.label} className="channel_performance_header_cell">
                                    <span className="ab-th">{h.label}<InfoTip text={h.tip} /></span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key} className="channel_performance_table_row">
                                <td className={cell}>
                                    <div className="ab-name">{r.name}</div>
                                    <div className="ab-sub">{r.key}</div>
                                </td>
                                <td className={cell}>{r.impressions.toLocaleString()}</td>
                                <td className={cell}>{r.conversions.toLocaleString()}</td>
                                <td className={cell}>
                                    {pct(r.rate, 2)}
                                    <div className="ab-sub">{pct(r.rateCi[0], 2)} – {pct(r.rateCi[1], 2)}</div>
                                </td>
                                <td className={cell}>{r.uplift == null ? '—' : `${r.uplift >= 0 ? '+' : ''}${pct(r.uplift)}`}</td>
                                <td className={cell}>{r.pValue == null ? '—' : r.pValue.toFixed(3)}</td>
                                <td className={cell}>{pct(r.probBeatsControl)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {variants.length > 1 && (
                <div className="ab-results-notes">
                    <p>
                        Each variant is compared with Control on its own. With several variants, one can look like a
                        winner by chance, and every arm takes longer to reach a result.
                    </p>
                </div>
            )}
        </div>
    );
}
